import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = 'https://ticobaterias.com/api';
const URL_REGISTRO = 'https://ticobaterias.com/rifa';

/** Igual que en el backend: 'Feria Expo Móvil' -> 'feria-expo-movil' (solo para la vista previa). */
const slugificar = (t: string) =>
  t.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

type Estado = 'borrador' | 'abierto' | 'cerrado' | 'finalizado';
type MarcaBono = { marca: string; entradas: number };
type FormatoFactura = 'ticobaterias' | 'electronica' | 'libre';

const MAX_MARCAS = 10;
const ENTRADAS_OPCIONES = [2, 3, 4, 5, 6, 7, 8, 9, 10];
const PALABRA: Record<number, string> = { 2: 'doble', 3: 'triple', 4: 'cuádruple' };
type EstadoRegistro = Estado | 'programado';

type FormSorteo = {
  nombre: string;
  slug: string;
  premio: string;
  descripcion: string;
  estado: Estado;
  marcas_bonificadas: MarcaBono[];
  no_repetir: boolean;
  requiere_factura: boolean;
  formato_factura: FormatoFactura;
  fecha_inicio: string; // valor del input datetime-local (hora local), '' = sin fecha
  fecha_fin: string;
  reglamento: string;
};

type Sorteo = Omit<FormSorteo, 'fecha_inicio' | 'fecha_fin'> & {
  id: number;
  url: string;
  fecha_inicio: string | null; // UTC desde el backend
  fecha_fin: string | null;
  registro: EstadoRegistro;
  personas: number;
  entradas_en_ruleta: number;
  ganadores: number;
  creado_en: string;
};

type Registro = {
  id: number;
  nombre: string;
  marca: string;
  factura: string;
  telefono: string;
  correo: string;
  entradas: number;
  origen: 'formulario' | 'manual';
  creado_en: string;
  gano: boolean;
  excluido: boolean;
};

type Ganador = {
  id: number;
  nombre: string;
  premio: string;
  marca: string;
  factura: string;
  telefono: string;
  correo: string;
  entradas: number;
  anulado: boolean;
  motivo_anulacion: string;
  creado_en: string;
};

type Pestana = 'participantes' | 'ganadores' | 'config' | 'registro';

const VACIO: FormSorteo = {
  nombre: '',
  slug: '',
  premio: '',
  descripcion: '',
  estado: 'borrador',
  marcas_bonificadas: [{ marca: 'LTH', entradas: 2 }],
  no_repetir: true,
  requiere_factura: true,
  formato_factura: 'ticobaterias',
  fecha_inicio: '',
  fecha_fin: '',
  reglamento: '',
};

const ESTADOS: { valor: Estado; etiqueta: string; ayuda: string; clase: string }[] = [
  { valor: 'borrador', etiqueta: 'Borrador', ayuda: 'En preparación. Nadie se puede registrar todavía.', clase: 'bg-gray-100 text-gray-600' },
  { valor: 'abierto', etiqueta: 'Abierto', ayuda: 'Recibe registros por su enlace y su QR (dentro de las fechas, si las pusiste).', clase: 'bg-green-100 text-green-700' },
  { valor: 'cerrado', etiqueta: 'Cerrado', ayuda: 'Ya no recibe registros, pero se puede seguir girando la ruleta.', clase: 'bg-yellow-100 text-yellow-700' },
  { valor: 'finalizado', etiqueta: 'Finalizado', ayuda: 'Sorteo terminado y archivado. No se puede girar.', clase: 'bg-blue-100 text-blue-700' },
];

const PROGRAMADO = { valor: 'programado', etiqueta: 'Programado', ayuda: '', clase: 'bg-purple-100 text-purple-700' };
const infoEstado = (e: EstadoRegistro) => (e === 'programado' ? PROGRAMADO : ESTADOS.find(x => x.valor === e) || ESTADOS[0]);

/** Fecha UTC del backend -> valor para <input type="datetime-local"> en hora local. */
const aInputLocal = (s: string | null) => {
  if (!s) return '';
  const d = new Date(/(Z|[+-]\d{2}:\d{2})$/.test(s) ? s : s + 'Z');
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
};

/** Valor del input (hora local) -> ISO con zona, para el backend. */
const aIso = (v: string) => (v ? new Date(v).toISOString() : null);

const fechaLarga = (v: string) =>
  v ? new Date(v).toLocaleString('es-CR', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' }) : '';

// Las fechas del backend vienen en UTC sin zona
const fecha = (s: string | null | undefined) =>
  s ? new Date(/(Z|[+-]\d{2}:\d{2})$/.test(s) ? s : s + 'Z').toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' }) : '';

const mensajeError = (e: any, porDefecto: string) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && d.mensaje) return d.mensaje;
  return porDefecto;
};

function Badge({ texto, clase }: { texto: string; clase: string }) {
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${clase}`}>{texto}</span>;
}

function Dato({ titulo, valor }: { titulo: string; valor: string | number }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="text-gray-500 text-xs font-medium">{titulo}</div>
      <div className="text-2xl font-bold text-azul mt-1">{valor}</div>
    </div>
  );
}

export default function Rifa() {
  const [sorteos, setSorteos] = useState<Sorteo[]>([]);
  const [selId, setSelId] = useState<number | null>(null);
  const [creando, setCreando] = useState(false);
  const [pestana, setPestana] = useState<Pestana>('participantes');
  const [form, setForm] = useState<FormSorteo>(VACIO);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [ganadores, setGanadores] = useState<Ganador[]>([]);
  const [manuales, setManuales] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [marcasSistema, setMarcasSistema] = useState<string[]>([]);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const sel = sorteos.find(s => s.id === selId) || null;

  const mostrarAviso = (tipo: 'ok' | 'error', texto: string) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };

  const cargarSorteos = (seleccionar?: number) =>
    axios.get(`${API}/rifa/sorteos`, { headers })
      .then(r => {
        const lista: Sorteo[] = r.data;
        setSorteos(lista);
        if (seleccionar) setSelId(seleccionar);
        else if (!selId && lista.length) setSelId((lista.find(s => s.estado === 'abierto') || lista[0]).id);
        else if (!lista.length) { setCreando(true); setPestana('config'); }
      })
      .catch(() => mostrarAviso('error', '❌ No se pudieron cargar los sorteos'));

  const cargarDetalle = (id: number) => {
    axios.get(`${API}/rifa/sorteos/${id}/registros`, { headers }).then(r => setRegistros(r.data)).catch(() => setRegistros([]));
    axios.get(`${API}/rifa/sorteos/${id}/ganadores`, { headers }).then(r => setGanadores(r.data)).catch(() => setGanadores([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cargarSorteos();
    axios.get(`${API}/rifa/marcas`).then(r => setMarcasSistema(r.data || [])).catch(() => setMarcasSistema([]));
  }, []);

  // ---- marcas patrocinadoras ----
  const cambiarBono = (i: number, cambios: Partial<MarcaBono>) =>
    cambiar('marcas_bonificadas', form.marcas_bonificadas.map((b, j) => (j === i ? { ...b, ...cambios } : b)));
  const quitarBono = (i: number) => cambiar('marcas_bonificadas', form.marcas_bonificadas.filter((_, j) => j !== i));
  const usadas = (excepto: number) =>
    new Set(form.marcas_bonificadas.filter((_, j) => j !== excepto).map(b => b.marca.toLowerCase()));
  const libres = (excepto: number) => marcasSistema.filter(m => !usadas(excepto).has(m.toLowerCase()));
  const agregarBono = () => {
    const disponible = libres(-1)[0];
    if (!disponible || form.marcas_bonificadas.length >= MAX_MARCAS) return;
    cambiar('marcas_bonificadas', [...form.marcas_bonificadas, { marca: disponible, entradas: 2 }]);
  };

  useEffect(() => {
    if (!sel || creando) return;
    const { nombre, slug, premio, descripcion, estado, no_repetir, requiere_factura } = sel;
    setForm({
      nombre, slug: slug || '', premio, descripcion, estado, no_repetir, requiere_factura,
      formato_factura: sel.formato_factura || 'ticobaterias',
      marcas_bonificadas: (sel.marcas_bonificadas || []).map(b => ({ ...b })),
      fecha_inicio: aInputLocal(sel.fecha_inicio),
      fecha_fin: aInputLocal(sel.fecha_fin),
      reglamento: sel.reglamento || '',
    });
    setErrores({});
    cargarDetalle(sel.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, creando]);

  const refrescar = () => {
    if (!selId) return;
    cargarSorteos(selId);
    cargarDetalle(selId);
  };

  const nuevo = () => {
    setCreando(true);
    setForm(VACIO);
    setErrores({});
    setPestana('config');
  };

  const cambiar = <K extends keyof FormSorteo>(campo: K, valor: FormSorteo[K]) => {
    setForm(f => ({ ...f, [campo]: valor }));
    setErrores(e => {
      const r = { ...e };
      delete r[campo as string];
      return r;
    });
  };

  const guardar = async () => {
    setOcupado(true);
    setErrores({});
    try {
      const datos = { ...form, fecha_inicio: aIso(form.fecha_inicio), fecha_fin: aIso(form.fecha_fin) };
      const r = creando
        ? await axios.post(`${API}/rifa/sorteos`, datos, { headers })
        : await axios.put(`${API}/rifa/sorteos/${selId}`, datos, { headers });
      setCreando(false);
      await cargarSorteos(r.data.id);
      mostrarAviso('ok', creando ? '✅ Sorteo creado' : '✅ Cambios guardados');
      if (creando) setPestana('registro');
    } catch (e: any) {
      setErrores(e?.response?.data?.detail?.errores || {});
      mostrarAviso('error', `⚠️ ${mensajeError(e, 'No se pudo guardar')}`);
    } finally {
      setOcupado(false);
    }
  };

  const eliminarSorteo = async () => {
    if (!sel || !window.confirm(`¿Eliminar el sorteo "${sel.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/rifa/sorteos/${sel.id}`, { headers });
      setSelId(null);
      setSorteos(s => s.filter(x => x.id !== sel.id));
      cargarSorteos();
      mostrarAviso('ok', '✅ Sorteo eliminado');
    } catch (e) {
      mostrarAviso('error', `⚠️ ${mensajeError(e, 'No se pudo eliminar')}`);
    }
  };

  const agregarManuales = async () => {
    if (!selId) return;
    setOcupado(true);
    try {
      const r = await axios.post(`${API}/rifa/sorteos/${selId}/manuales`, { nombres: manuales }, { headers });
      setManuales('');
      refrescar();
      mostrarAviso('ok', `✅ Se agregaron ${r.data.agregados} participante${r.data.agregados === 1 ? '' : 's'}`);
    } catch (e) {
      mostrarAviso('error', `⚠️ ${mensajeError(e, 'No se pudieron agregar')}`);
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (r: Registro) => {
    if (!window.confirm(`¿Quitar a ${r.nombre} de la ruleta?`)) return;
    try {
      await axios.delete(`${API}/rifa/registros/${r.id}`, { headers });
      refrescar();
      mostrarAviso('ok', '✅ Participante quitado');
    } catch (e) {
      mostrarAviso('error', `⚠️ ${mensajeError(e, 'No se pudo quitar')}`);
    }
  };

  const anular = async (g: Ganador) => {
    const motivo = window.prompt(`¿Por qué se anula el premio de ${g.nombre}? (por ejemplo: no se presentó)`, 'No se presentó');
    if (motivo === null) return;
    try {
      await axios.post(`${API}/rifa/ganadores/${g.id}/anular`, { motivo }, { headers });
      refrescar();
      mostrarAviso('ok', '✅ Ganador anulado. Puedes volver a girar la ruleta.');
    } catch (e) {
      mostrarAviso('error', `⚠️ ${mensajeError(e, 'No se pudo anular')}`);
    }
  };

  const exportar = async () => {
    if (!sel) return;
    try {
      const r = await axios.get(`${API}/rifa/sorteos/${sel.id}/exportar`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rifa-${sel.nombre.replace(/[^\w-]+/g, '-').toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      mostrarAviso('error', '❌ No se pudo descargar');
    }
  };

  const copiar = (texto: string) =>
    navigator.clipboard?.writeText(texto).then(
      () => mostrarAviso('ok', '📋 Enlace copiado'),
      () => mostrarAviso('error', 'No se pudo copiar, selecciónalo a mano')
    );

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return registros;
    return registros.filter(r =>
      [r.nombre, r.marca, r.factura, r.telefono, r.correo].some(v => (v || '').toLowerCase().includes(q))
    );
  }, [registros, busqueda]);

  const input = (campo: keyof FormSorteo, placeholder = '', tipo = 'text') => (
    <>
      <input
        type={tipo}
        value={String(form[campo])}
        onChange={e => cambiar(campo, (tipo === 'number' ? Number(e.target.value) : e.target.value) as never)}
        placeholder={placeholder}
        className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none ${errores[campo] ? 'border-red-400' : 'border-gray-200 focus:border-azul'}`}
      />
      {errores[campo] && <p className="text-xs text-red-500 font-bold mt-1">{errores[campo]}</p>}
    </>
  );

  // ---------------- pestañas ----------------

  const renderParticipantes = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
          <h2 className="font-bold text-azul">👥 Participantes ({registros.length})</h2>
          <div className="flex gap-2 flex-wrap">
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar nombre, factura, teléfono..."
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-azul w-64 max-w-full" />
            <button onClick={exportar} disabled={!registros.length}
              className="border border-azul text-azul rounded-lg px-3 py-2 text-sm font-bold hover:bg-blue-50 disabled:opacity-40">
              ⬇ Excel (CSV)
            </button>
          </div>
        </div>

        {registros.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-8">
            Todavía no hay participantes. Comparte el enlace o el QR de la pestaña <b>Registro y QR</b>, o agrégalos a mano aquí abajo.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Nombre</th>
                  <th className="py-2 pr-3 font-medium">Marca</th>
                  <th className="py-2 pr-3 font-medium">Factura</th>
                  <th className="py-2 pr-3 font-medium">Contacto</th>
                  <th className="py-2 pr-3 font-medium">Entradas</th>
                  <th className="py-2 pr-3 font-medium">Fecha</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtrados.map(r => (
                  <tr key={r.id} className={r.excluido ? 'opacity-50' : ''}>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-gray-800">{r.nombre}</div>
                      <div className="flex gap-1 mt-0.5">
                        {r.gano && <Badge texto="🏆 Ganó" clase="bg-yellow-100 text-yellow-800" />}
                        {r.excluido && <Badge texto="Ya ganó con otra factura" clase="bg-gray-100 text-gray-500" />}
                        {r.origen === 'manual' && <Badge texto="A mano" clase="bg-gray-100 text-gray-500" />}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600">{r.marca || '—'}</td>
                    <td className="py-2.5 pr-3 text-gray-600 font-mono text-xs">{r.factura || '—'}</td>
                    <td className="py-2.5 pr-3 text-gray-600 text-xs">
                      {r.telefono && <div>{r.telefono}</div>}
                      {r.correo && <div className="break-all">{r.correo}</div>}
                      {!r.telefono && !r.correo && '—'}
                    </td>
                    <td className="py-2.5 pr-3">
                      {r.entradas > 1 ? <Badge texto={`×${r.entradas}`} clase="bg-yellow-400 text-azul" /> : <span className="text-gray-500">1</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-400 text-xs whitespace-nowrap">{fecha(r.creado_en)}</td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => quitar(r)}
                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-red-50 hover:border-red-300 hover:text-red-600">
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtrados.length === 0 && <div className="text-center text-gray-400 text-sm py-6">Sin coincidencias</div>}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-bold text-azul mb-1">✍️ Agregar participantes a mano</h2>
        <p className="text-gray-500 text-sm mb-3">Un nombre por línea. Puedes pegar una lista completa. Entran con 1 participación.</p>
        <textarea value={manuales} onChange={e => setManuales(e.target.value)} rows={5}
          placeholder={'María Rodríguez\nCarlos Jiménez\nAna Solano'}
          className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-azul resize-y" />
        <button onClick={agregarManuales} disabled={ocupado || !manuales.trim()}
          className="mt-3 bg-azul text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-azul-dark disabled:opacity-50">
          ➕ Agregar a la ruleta
        </button>
      </div>
    </div>
  );

  const renderGanadores = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h2 className="font-bold text-azul mb-1">🏆 Historial de ganadores</h2>
      <p className="text-gray-500 text-sm mb-4">
        Si un ganador no reclama el premio, usa <b>Anular</b> y vuelve a girar. Queda registrado el motivo; nada se borra.
      </p>
      {ganadores.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-8">Todavía no se ha girado la ruleta en este sorteo.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Ganador</th>
                <th className="py-2 pr-3 font-medium">Premio</th>
                <th className="py-2 pr-3 font-medium">Factura</th>
                <th className="py-2 pr-3 font-medium">Contacto</th>
                <th className="py-2 pr-3 font-medium">Fecha</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ganadores.map(g => (
                <tr key={g.id} className={g.anulado ? 'opacity-60' : ''}>
                  <td className="py-2.5 pr-3">
                    <div className={`font-bold ${g.anulado ? 'line-through text-gray-500' : 'text-azul'}`}>{g.nombre}</div>
                    <div className="text-xs text-gray-400">{g.marca}{g.entradas > 1 ? ` · ×${g.entradas}` : ''}</div>
                    {g.anulado && <div className="text-xs text-red-500 font-bold">Anulado: {g.motivo_anulacion}</div>}
                  </td>
                  <td className="py-2.5 pr-3 text-gray-700">{g.premio || '—'}</td>
                  <td className="py-2.5 pr-3 text-gray-600 font-mono text-xs">{g.factura || '—'}</td>
                  <td className="py-2.5 pr-3 text-gray-600 text-xs">
                    {g.telefono && <div>{g.telefono}</div>}
                    {g.correo && <div className="break-all">{g.correo}</div>}
                  </td>
                  <td className="py-2.5 pr-3 text-gray-400 text-xs whitespace-nowrap">{fecha(g.creado_en)}</td>
                  <td className="py-2.5 text-right">
                    {!g.anulado && (
                      <button onClick={() => anular(g)}
                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-red-50 hover:border-red-300 hover:text-red-600">
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderConfig = () => (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
      <h2 className="font-bold text-azul">{creando ? '✨ Nuevo sorteo' : '⚙️ Configuración del sorteo'}</h2>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-bold text-gray-700 block mb-1.5">Nombre del sorteo</label>
          {input('nombre', 'Ej. Rifa Expo Octubre 2026')}
        </div>
        <div>
          <label className="text-sm font-bold text-gray-700 block mb-1.5">Premio actual</label>
          {input('premio', 'Ej. Batería LTH 24')}
          <p className="text-xs text-gray-400 mt-1">Se puede cambiar entre giros desde la pantalla de la ruleta.</p>
        </div>
      </div>

      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1.5">🔗 Enlace del sorteo</label>
        <div className={`flex items-center border rounded-lg overflow-hidden ${errores.slug ? 'border-red-400' : 'border-gray-200 focus-within:border-azul'}`}>
          <span className="bg-gray-50 text-gray-500 text-sm px-3 py-2.5 border-r border-gray-200 whitespace-nowrap">ticobaterias.com/rifa/</span>
          <input value={form.slug} onChange={e => cambiar('slug', e.target.value.toLowerCase())}
            placeholder={slugificar(form.nombre) || 'feria-expo-movil'}
            className="flex-1 min-w-0 px-3 py-2.5 text-sm focus:outline-none" />
        </div>
        {errores.slug
          ? <p className="text-xs text-red-500 font-bold mt-1">{errores.slug}</p>
          : <p className="text-xs text-gray-400 mt-1">
              Si lo dejas vacío se arma solo con el nombre. Ojo: si lo cambias después de imprimir el QR, el QR viejo deja de funcionar.
            </p>}
      </div>

      <div>
        <label className="text-sm font-bold text-gray-700 block mb-2">Tipo de sorteo</label>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { valor: true, titulo: '🧾 Contra factura', ayuda: 'Para clientes que compraron. Pide el número de factura; cada factura participa una vez.' },
            { valor: false, titulo: '🎉 Abierto al público', ayuda: 'Ferias y eventos. No pide factura; cada persona participa una vez por teléfono.' },
          ].map(t => (
            <button key={String(t.valor)} type="button" onClick={() => cambiar('requiere_factura', t.valor)}
              className={`text-left border rounded-xl p-3 transition-colors ${form.requiere_factura === t.valor ? 'border-azul bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="text-sm font-bold text-gray-800">{t.titulo}</div>
              <div className="text-xs text-gray-500 mt-1 leading-snug">{t.ayuda}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1.5">Texto para el formulario (opcional)</label>
        <textarea value={form.descripcion} onChange={e => cambiar('descripcion', e.target.value)} rows={3}
          placeholder="Ej. Participan todas las compras del 1 al 31 de octubre. El sorteo es el sábado 2 de noviembre a las 3 p.m."
          className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-azul resize-y" />
      </div>

      <div>
        <label className="text-sm font-bold text-gray-700 block mb-2">Estado</label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {ESTADOS.map(e => (
            <button key={e.valor} type="button" onClick={() => cambiar('estado', e.valor)}
              className={`text-left border rounded-xl p-3 transition-colors ${form.estado === e.valor ? 'border-azul bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <Badge texto={e.etiqueta} clase={e.clase} />
              <div className="text-xs text-gray-500 mt-1.5 leading-snug">{e.ayuda}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1.5">📜 Condiciones / reglamento del sorteo</label>
        <textarea value={form.reglamento} onChange={e => cambiar('reglamento', e.target.value)} rows={6}
          placeholder={'1. Participan las compras realizadas del 1 al 31 de octubre de 2026.\n2. Cada factura participa una sola vez. Baterías LTH participan doble.\n3. El sorteo se realiza el sábado 2 de noviembre a las 3:00 p. m. en el local.\n4. El ganador debe presentar la factura original y su cédula.\n5. El premio no es canjeable por dinero.'}
          className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none resize-y ${errores.reglamento ? 'border-red-400' : 'border-gray-200 focus:border-azul'}`} />
        {errores.reglamento && <p className="text-xs text-red-500 font-bold mt-1">{errores.reglamento}</p>}
        <p className="text-xs text-gray-400 mt-1">
          El cliente lo puede abrir con el enlace «Ver las condiciones del sorteo», justo encima del botón <b>Acepto y participo</b>.
          Al participar también autoriza recibir ofertas (queda en la base de <b>Clientes</b>).
        </p>
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex justify-between items-center gap-3 mb-3 flex-wrap">
          <label className="text-sm font-bold text-gray-700">📅 Fechas del registro (opcional)</label>
          {(form.fecha_inicio || form.fecha_fin) && (
            <button type="button" onClick={() => { cambiar('fecha_inicio', ''); cambiar('fecha_fin', ''); }}
              className="text-xs text-gray-500 hover:text-red-600 underline">Quitar fechas</button>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-500 block mb-1">Abre el registro</label>
            {input('fecha_inicio', '', 'datetime-local')}
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 block mb-1">Cierra el registro</label>
            {input('fecha_fin', '', 'datetime-local')}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          {form.fecha_inicio || form.fecha_fin ? (
            <>
              Con el estado en <b>Abierto</b>:
              {form.fecha_inicio ? <> el registro se abre solo el <b>{fechaLarga(form.fecha_inicio)}</b></> : <> el registro está abierto desde ya</>}
              {form.fecha_fin ? <> y se cierra solo el <b>{fechaLarga(form.fecha_fin)}</b>.</> : <> y se cierra a mano.</>}
              {' '}Después del cierre se puede seguir girando la ruleta.
            </>
          ) : (
            <>Sin fechas, el registro se abre y se cierra a mano con el estado.</>
          )}
        </p>
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex justify-between items-center gap-3 mb-1 flex-wrap">
          <label className="text-sm font-bold text-gray-700">⭐ Marcas patrocinadoras ({form.marcas_bonificadas.length}/{MAX_MARCAS})</label>
          <button type="button" onClick={agregarBono}
            disabled={form.marcas_bonificadas.length >= MAX_MARCAS || libres(-1).length === 0}
            className="text-xs border border-azul text-azul rounded-lg px-3 py-1.5 font-bold hover:bg-blue-50 disabled:opacity-40">
            ＋ Agregar marca
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Quien tenga batería de una de estas marcas entra más veces a la ruleta. Las marcas salen de los productos del catálogo.
        </p>

        {form.marcas_bonificadas.length === 0 ? (
          <div className="text-xs text-gray-400 py-2">Sin marcas patrocinadoras: todos participan una vez.</div>
        ) : (
          <div className="space-y-2">
            {form.marcas_bonificadas.map((b, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={b.marca} onChange={e => cambiarBono(i, { marca: e.target.value })}
                  className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-azul">
                  {!marcasSistema.some(m => m.toLowerCase() === b.marca.toLowerCase()) && b.marca && (
                    <option value={b.marca}>{b.marca} (ya no está en el catálogo)</option>
                  )}
                  {libres(i).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={b.entradas} onChange={e => cambiarBono(i, { entradas: Number(e.target.value) })}
                  className="w-40 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-azul">
                  {ENTRADAS_OPCIONES.map(n => (
                    <option key={n} value={n}>×{n}{PALABRA[n] ? ` (${PALABRA[n]})` : ''}</option>
                  ))}
                </select>
                <button type="button" onClick={() => quitarBono(i)} aria-label={`Quitar ${b.marca}`}
                  className="w-9 h-9 shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-300">×</button>
              </div>
            ))}
          </div>
        )}
        {errores.marcas_bonificadas && <p className="text-xs text-red-500 font-bold mt-2">{errores.marcas_bonificadas}</p>}
        {marcasSistema.length === 0 && (
          <p className="text-xs text-yellow-700 mt-2">No hay marcas en los productos del catálogo. Agrega la marca a los productos para poder elegirla aquí.</p>
        )}
        {!form.requiere_factura && form.marcas_bonificadas.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 mt-3">
            ⚠️ En un sorteo abierto no hay factura para comprobar la marca: cualquiera puede elegir una marca patrocinadora para participar más veces.
            Si no lo vas a verificar al entregar el premio, quita las marcas.
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex gap-3 items-start cursor-pointer">
          <input type="checkbox" checked={form.no_repetir} onChange={e => cambiar('no_repetir', e.target.checked)} className="mt-1 w-4 h-4" />
          <span className="text-sm text-gray-700">
            <b>No repetir ganadores.</b> Quien gana sale de la ruleta, incluidas sus otras facturas (mismo teléfono o correo).
          </span>
        </label>
        {form.requiere_factura && (
          <div>
            <div className="text-sm font-bold text-gray-700 mb-2">🧾 Número de factura que pide el formulario</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {([
                { valor: 'ticobaterias', titulo: 'TicoBaterías', ejemplo: 'DES-14505', ayuda: '«Factura Contado No.», abajo a la derecha. Recomendado.' },
                { valor: 'electronica', titulo: 'Factura electrónica', ejemplo: '00100001010000014505', ayuda: 'Los 20 dígitos de «N. Factura Electrónica».' },
                { valor: 'libre', titulo: 'Cualquier número', ejemplo: 'Lo que escriban', ayuda: 'Para facturas de otro sistema. No valida el formato.' },
              ] as { valor: FormatoFactura; titulo: string; ejemplo: string; ayuda: string }[]).map(f => (
                <button key={f.valor} type="button" onClick={() => cambiar('formato_factura', f.valor)}
                  className={`text-left border rounded-xl p-3 transition-colors ${form.formato_factura === f.valor ? 'border-azul bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                  <div className="text-sm font-bold text-gray-800">{f.titulo}</div>
                  <div className="text-xs font-mono text-azul mt-0.5 break-all">{f.ejemplo}</div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug">{f.ayuda}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">Cada factura participa una sola vez en todas las rifas, aunque la escriban con espacios, guiones o el «CR» final.</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <button onClick={guardar} disabled={ocupado}
          className="flex-1 min-w-[200px] bg-azul text-white py-3 rounded-xl font-bold hover:bg-azul-dark disabled:opacity-50">
          {ocupado ? '⏳ Guardando...' : creando ? '✨ Crear sorteo' : '💾 Guardar cambios'}
        </button>
        {creando && sorteos.length > 0 && (
          <button onClick={() => { setCreando(false); setPestana('participantes'); }}
            className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
            Cancelar
          </button>
        )}
        {!creando && sel && sel.personas === 0 && (
          <button onClick={eliminarSorteo}
            className="px-5 py-3 rounded-xl border border-red-200 text-red-600 font-bold hover:bg-red-50">
            🗑️ Eliminar
          </button>
        )}
      </div>
    </div>
  );

  const renderRegistro = () => {
    const url = sel?.url || URL_REGISTRO;
    const qr = sel?.slug ? `${API}/rifa/qr/${sel.slug}.svg` : `${API}/rifa/qr.svg`;
    return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-bold text-azul mb-1">🔗 Enlace de este sorteo</h2>
        <p className="text-gray-500 text-sm mb-4">Compártelo en redes, por WhatsApp o en la pantalla del evento. Solo registra en <b>este</b> sorteo.</p>
        <div className="flex gap-2">
          <input readOnly value={url} onFocus={e => e.target.select()}
            className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50" />
          <button onClick={() => copiar(url)} className="border border-azul text-azul rounded-lg px-4 text-sm font-bold hover:bg-blue-50">
            📋 Copiar
          </button>
        </div>
        <div className={`mt-4 rounded-xl p-4 text-sm ${sel?.registro === 'abierto' ? 'bg-green-50 text-green-700' : sel?.registro === 'programado' ? 'bg-purple-50 text-purple-700' : 'bg-yellow-50 text-yellow-800'}`}>
          {sel?.registro === 'abierto' ? (
            <>✅ Este sorteo está <b>abierto</b>: quien entre al enlace queda registrado aquí.
              {sel.fecha_fin && <> Se cierra solo el <b>{fechaLarga(aInputLocal(sel.fecha_fin))}</b>.</>}</>
          ) : sel?.registro === 'programado' ? (
            <>🕒 Este sorteo está <b>programado</b>: el enlace muestra una cuenta regresiva y el registro se abre solo el <b>{fechaLarga(aInputLocal(sel.fecha_inicio))}</b>.</>
          ) : (
            <>⚠️ Este sorteo no está recibiendo registros: su enlace muestra que el registro está cerrado. Para abrirlo, cambia el estado en <b>Configuración</b>.</>
          )}
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="inline-block mt-4 text-sm text-azul font-bold hover:underline">
          Ver el formulario como lo ve el cliente →
        </a>
        <p className="text-xs text-gray-400 mt-4">
          También existe <b>ticobaterias.com/rifa</b>, que muestra todos los sorteos abiertos para que la persona elija (si hay uno solo, entra directo).
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
        <h2 className="font-bold text-azul mb-1">📲 QR de este sorteo</h2>
        <p className="text-gray-500 text-sm mb-4">Imprímelo para el local o el evento. Quien lo escanee queda en <b>{sel?.nombre}</b>.</p>
        <img src={qr} alt={`QR del sorteo ${sel?.nombre || ''}`} className="w-48 h-48 mx-auto border border-gray-100 rounded-xl p-2 bg-white" />
        <a href={qr} target="_blank" rel="noreferrer"
          className="inline-block mt-4 border border-azul text-azul rounded-lg px-4 py-2 text-sm font-bold hover:bg-blue-50">
          🖨️ Abrir QR para imprimir
        </a>
      </div>
    </div>
    );
  };

  // ---------------- página ----------------

  const PESTANAS: { id: Pestana; etiqueta: string }[] = [
    { id: 'participantes', etiqueta: `👥 Participantes${sel ? ` (${sel.personas})` : ''}` },
    { id: 'ganadores', etiqueta: `🏆 Ganadores${sel ? ` (${sel.ganadores})` : ''}` },
    { id: 'config', etiqueta: '⚙️ Configuración' },
    { id: 'registro', etiqueta: '📲 Registro y QR' },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl text-azul">Rifa / Ruleta</h1>
          <p className="text-gray-500 text-sm">Sorteos con registro de clientes y ruleta para eventos</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {aviso && (
            <div className={`px-4 py-2 rounded-xl text-sm font-bold ${aviso.tipo === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {aviso.texto}
            </div>
          )}
          <button onClick={nuevo} className="bg-rojo text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90">
            ＋ Nuevo sorteo
          </button>
        </div>
      </div>

      {sorteos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          {sorteos.map(s => (
            <button key={s.id} onClick={() => { setCreando(false); setSelId(s.id); if (pestana === 'config' && creando) setPestana('participantes'); }}
              className={`shrink-0 text-left border rounded-xl px-4 py-3 transition-colors ${!creando && s.id === selId ? 'border-azul bg-white shadow-sm' : 'border-gray-200 bg-white/60 hover:bg-white'}`}>
              <div className="font-bold text-sm text-azul max-w-[220px] truncate">{s.requiere_factura ? '🧾' : '🎉'} {s.nombre}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge texto={infoEstado(s.registro).etiqueta} clase={infoEstado(s.registro).clase} />
                <span className="text-xs text-gray-400">{s.personas} personas</span>
              </div>
              {s.registro === 'programado' && s.fecha_inicio && (
                <div className="text-xs text-purple-600 mt-1">Abre {fecha(s.fecha_inicio)}</div>
              )}
              {s.registro === 'abierto' && s.fecha_fin && (
                <div className="text-xs text-green-700 mt-1">Cierra {fecha(s.fecha_fin)}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {creando ? (
        renderConfig()
      ) : sel ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Dato titulo="Personas registradas" valor={sel.personas} />
            <Dato titulo="Entradas en la ruleta" valor={sel.entradas_en_ruleta} />
            <Dato titulo="Ganadores" valor={sel.ganadores} />
            <div className="bg-azul rounded-2xl p-4 flex flex-col justify-between">
              <div className="text-white/60 text-xs font-medium">Premio actual</div>
              <div className="text-white font-bold truncate">{sel.premio || 'Sin definir'}</div>
              <a href={`/rifa/pantalla/${sel.id}`} target="_blank" rel="noreferrer"
                className="mt-2 bg-rojo text-white text-center rounded-lg py-2 text-sm font-bold hover:opacity-90">
                🎡 Abrir ruleta
              </a>
            </div>
          </div>

          <div className="inline-flex flex-wrap bg-white border border-gray-200 rounded-xl p-1 mb-6">
            {PESTANAS.map(p => (
              <button key={p.id} onClick={() => setPestana(p.id)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${pestana === p.id ? 'bg-azul text-white' : 'text-gray-500 hover:text-azul'}`}>
                {p.etiqueta}
              </button>
            ))}
          </div>

          {pestana === 'participantes' && renderParticipantes()}
          {pestana === 'ganadores' && renderGanadores()}
          {pestana === 'config' && renderConfig()}
          {pestana === 'registro' && renderRegistro()}
        </>
      ) : (
        <div className="text-center text-gray-400 py-12">Cargando...</div>
      )}
    </div>
  );
}
